import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Monitor } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function PosDailyReportCard({ formatCurrency }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pos/reports/daily?date=${date}`, auth());
      setReport(r.data);
    } catch { setReport(null); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  if (!report) return null;

  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-5" data-testid="pos-daily-report">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Monitor className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-wt-text-primary">POS Daily Sales</h2>
        </div>
        <DateInput  value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-8" data-testid="pos-report-date" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-sm">
        <div><p className="text-xs text-wt-text-muted">Sales</p><p className="font-semibold text-wt-text-primary">{report.sales_count} · {formatCurrency(report.gross_sales)}</p></div>
        <div><p className="text-xs text-wt-text-muted">Returns</p><p className="font-semibold text-red-400">{report.returns_count} · −{formatCurrency(report.returns_total)}</p></div>
        <div><p className="text-xs text-wt-text-muted">Net</p><p className="font-semibold text-wt-gold" data-testid="pos-report-net">{formatCurrency(report.net_sales)}</p></div>
        <div><p className="text-xs text-wt-text-muted">Cash / Card / Link</p><p className="font-semibold text-wt-text-primary">{formatCurrency(report.payment_mix.cash)} / {formatCurrency(report.payment_mix.card)} / {formatCurrency(report.payment_mix.link)}</p></div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 text-xs">
        <div>
          <p className="text-wt-text-muted mb-1">By store</p>
          {Object.entries(report.by_store).map(([k, v]) => (
            <div key={k} className="flex justify-between text-wt-text-primary py-0.5 border-b border-wt-border/30"><span>{k}</span><span>{v.sales} · {formatCurrency(v.amount)}</span></div>
          ))}
          {Object.keys(report.by_store).length === 0 && <p className="text-wt-text-muted">No sales</p>}
        </div>
        <div>
          <p className="text-wt-text-muted mb-1">By staff</p>
          {Object.entries(report.by_staff).map(([k, v]) => (
            <div key={k} className="flex justify-between text-wt-text-primary py-0.5 border-b border-wt-border/30"><span>{k}</span><span>{v.sales} · {formatCurrency(v.amount)}</span></div>
          ))}
          {Object.keys(report.by_staff).length === 0 && <p className="text-wt-text-muted">No sales</p>}
        </div>
      </div>
      {Object.keys(report.order_types).length > 0 && (
        <p className="text-[11px] text-wt-text-muted mt-2">
          Order types: {Object.entries(report.order_types).map(([k, v]) => `${k.replace('_', '-')} ${v}`).join(' · ')}
        </p>
      )}
    </div>
  );
}
